import { z } from "zod";
import { SUPPORTED_LANGUAGE_CODES } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

export const DETECTION_METHODS = ["ai", "deterministic"] as const;
export type DetectionMethod = (typeof DETECTION_METHODS)[number];

export const detectLanguageStep = defineStepConfig({
  type: "detect_language",
  name: "Detect Language",
  description:
    "Detect the primary language of a text sample. Returns an ISO 639-1 code, language name, and confidence score.",
  category: "AI & Agents",

  inputSchema: z.object({
    text: z.string().min(1).describe("The text to detect the language of."),
  }),

  outputSchema: z.object({
    language: z
      .string()
      .describe("ISO 639-1 two-letter language code (e.g. 'en', 'fr')."),
    languageName: z.string().describe("English name of the detected language."),
    confidence: z
      .number()
      .describe("Detection confidence score from 0.0 to 1.0."),
  }),

  configSchema: z.object({
    candidateLanguages: z
      .array(z.enum(SUPPORTED_LANGUAGE_CODES))
      .min(1)
      .optional()
      .describe(
        "Languages to detect from. When omitted, all supported languages are used.",
      ),
    method: z
      .enum(DETECTION_METHODS)
      .default("ai")
      .describe(
        "Detection method: 'ai' uses AI inference, 'deterministic' uses trigram analysis (faster, no API cost).",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "3 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
